/**
 * A WebSocket that reconnects, and nothing else.
 *
 * Native `WebSocket` only — ARCHITECTURE.md rule 3 forbids new npm packages, so
 * there is no socket.io here and the protocol is plain JSON text frames.
 *
 * The queue is deliberately tiny. Everything this game sends is a *state*, not
 * a *command*: a position from 900 ms ago is worthless, so a dropped frame is
 * simply dropped rather than replayed on reconnect. The two exceptions are the
 * join handshake, which is re-sent automatically on every open, and chat.
 */

const BACKOFF_MIN = 1000;
const BACKOFF_MAX = 10000;

export class NetSocket {
  /**
   * @param {object} opts
   * @param {string} opts.url
   * @param {(msg:object)=>void} opts.onMessage
   * @param {()=>void} [opts.onOpen]
   * @param {(reason:string)=>void} [opts.onClose]
   */
  constructor({ url, onMessage, onOpen, onClose }) {
    this.url = url;
    this.onMessage = onMessage;
    this.onOpen = onOpen ?? (() => {});
    this.onClose = onClose ?? (() => {});

    this.ws = null;
    /** 'idle' | 'connecting' | 'open' | 'closed' */
    this.status = 'idle';
    this.attempts = 0;
    this.lastError = '';
    this.bytesIn = 0;
    this.bytesOut = 0;
    this.msgsIn = 0;
    this.msgsOut = 0;

    this._retryTimer = 0;
    this._stopped = false;
    /** Messages queued while the socket was down. Bounded, oldest dropped. */
    this._pending = [];
  }

  connect() {
    if (this._stopped) return;
    if (this.ws && (this.status === 'open' || this.status === 'connecting')) return;
    this.status = 'connecting';
    let ws;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      this.lastError = String(err?.message ?? err);
      this.status = 'closed';
      this._scheduleRetry();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.status = 'open';
      this.attempts = 0;
      this.lastError = '';
      console.info(`[net] connected to ${this.url}`);
      this.onOpen();
      const queued = this._pending;
      this._pending = [];
      for (const m of queued) this.send(m);
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      this.bytesIn += ev.data.length;
      this.msgsIn++;
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.onMessage(msg);
    };

    ws.onerror = () => {
      // The event carries nothing useful; the close that follows has the code.
      this.lastError = 'socket error';
    };

    ws.onclose = (ev) => {
      const wasOpen = this.status === 'open';
      this.status = 'closed';
      this.ws = null;
      if (!this._stopped) {
        this.lastError = ev.reason || `closed (${ev.code})`;
        if (wasOpen) console.warn(`[net] disconnected: ${this.lastError}`);
        this.onClose(this.lastError);
        this._scheduleRetry();
      }
    };
  }

  /** Queue a message for the next open socket. Only for things worth keeping. */
  queue(obj) {
    if (this.status === 'open') return this.send(obj);
    if (this._pending.length > 8) this._pending.shift();
    this._pending.push(obj);
    return false;
  }

  send(obj) {
    if (this.status !== 'open' || !this.ws) return false;
    let text;
    try {
      text = JSON.stringify(obj);
    } catch {
      return false;
    }
    try {
      this.ws.send(text);
    } catch {
      return false;
    }
    this.bytesOut += text.length;
    this.msgsOut++;
    return true;
  }

  /** Drive the reconnect clock. Called once a frame; no timers of its own, so
   *  a paused tab does not pile up retries. */
  update(dt) {
    if (this._stopped || this._retryTimer <= 0) return;
    this._retryTimer -= dt;
    if (this._retryTimer <= 0) this.connect();
  }

  _scheduleRetry() {
    this.attempts++;
    const ms = Math.min(BACKOFF_MAX, BACKOFF_MIN * 2 ** Math.min(6, this.attempts - 1));
    this._retryTimer = ms / 1000;
  }

  close() {
    this._stopped = true;
    this._pending.length = 0;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* already gone */
      }
      this.ws = null;
    }
    this.status = 'closed';
  }
}
